import type { ExitStrategyProps } from './types';
import React, { type FC } from 'react';
import {
  Badge,
  Button,
  Card,
  Flex,
  Heading,
  Icon,
  List,
  ListItem,
  LogoBox,
  Section,
  Text,
} from '@highereducation/hen-ui';
import { interpolateCtaUrl } from '../../utils/interpolateCtaUrl';

const SCHOOL_LOGO_BASE_URL =
  'https://simple-storage-server.highereducation.com';

/**
 * Two-column ExitStrategy used inside the Recommender popup (the modal
 * surfaced from `ResultsPage` after a successful lead submission). The
 * drawer-based ExitStrategy (`./Default`) keeps its single-column
 * scrollable layout — this variant only renders inside the modal.
 *
 * Left rail: school context (logo, name, program, "now accepting"
 * badge) plus the application / continue-browsing CTAs.
 * Right pane: "Congratulations" header and the supporting info cards
 * (advisor expectations + school value props).
 */
const ExitStrategyRecommender: FC<ExitStrategyProps> = ({
  firstName,
  schoolName,
  schoolSlug,
  programName,
  valueProps,
  applicationCtaUrl,
  applicationCtaLabel,
  onCloseExperience,
  extraCta,
  lead,
  partnerIdentifiers,
}) => {
  const schoolLogoUrl = schoolSlug
    ? `${SCHOOL_LOGO_BASE_URL}/${schoolSlug}.png?w=130&h=80`
    : undefined;
  const getExtraCtaHref = () => {
    if (extraCta?.enabled !== true || !extraCta.destination?.url) {
      return undefined;
    }
    const result = interpolateCtaUrl({
      baseUrl: extraCta.destination.url,
      staticParams: extraCta.destination.staticParams,
      fieldMap: extraCta.destination.fieldMap,
      missingValue: extraCta.destination.missingValue,
      sources: { lead, partnerIdentifiers },
    });
    return 'href' in result ? result.href : undefined;
  };
  const extraCtaHref = getExtraCtaHref();
  const extraCtaLabel =
    extraCta?.label ?? extraCta?.destination?.label ?? 'Learn more';

  const handleExternalCta = (
    e: React.MouseEvent<HTMLButtonElement>,
    url: string,
  ) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleExtraCta = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const href = getExtraCtaHref();
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Section
      variant='default'
      padding='none'
      gap={null}
      className='relative flex flex-col lg:flex-row overflow-hidden rounded-2xl'
    >
      {onCloseExperience && (
        <Button
          type='button'
          variant='iconOnly'
          size='sm'
          iconBefore='x'
          aria-label='Close'
          className='absolute right-4 top-4 z-20'
          onClick={onCloseExperience}
        />
      )}
      <Flex
        direction='column'
        gap='lg'
        className='lg:w-2/5 bg-white px-8 py-7 shrink-0'
      >
        {schoolLogoUrl && (
          <LogoBox src={schoolLogoUrl} alt={schoolName ?? ''} />
        )}

        <Flex direction='column' gap='xs' className='min-w-0'>
          <Heading variant='h4' className='truncate'>
            {schoolName}
          </Heading>
          {programName && (
            <Text variant='b4' color='subtext'>
              {programName}
            </Text>
          )}
        </Flex>

        <Badge variant='light' size='lg' className='gap-2 self-start'>
          <Icon icon='clock' size='sm' aria-hidden />
          Now Accepting New Students.
        </Badge>

        <Flex direction='column' gap='md'>
          <Heading variant='h6'>Next Steps</Heading>
          <Flex direction='column' gap='sm'>
            {applicationCtaUrl && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={(e) => handleExternalCta(e, applicationCtaUrl)}
              >
                {applicationCtaLabel || 'Start Your Application'}
              </Button>
            )}
            {extraCtaHref && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={handleExtraCta}
              >
                {extraCtaLabel}
              </Button>
            )}
            <Button
              variant='primaryOutline'
              size='lg'
              className='w-full'
              onClick={onCloseExperience}
            >
              Continue Browsing Programs
            </Button>
          </Flex>
        </Flex>

        <Text variant='b5' color='subtext'>
          Your information will never be shared with a school without your
          consent.
        </Text>
      </Flex>

      <Flex
        direction='column'
        gap='lg'
        className='lg:w-3/5 bg-secondary-50 px-8 py-7 flex-1'
      >
        <Flex direction='column' gap='xs'>
          <Heading variant='h3'>
            Congratulations{firstName ? `, ${firstName}` : ''}!
          </Heading>
          <Text variant='b4' color='subtext'>
            What&apos;s next?
          </Text>
        </Flex>

        <Card variant='background-white' rounded='lg'>
          <Card.Content>
            <Heading variant='h5'>What to expect from the Advisor</Heading>
            <List bulletStyle='bullet'>
              <ListItem>
                Hands-on assistance with your application process
              </ListItem>
              <ListItem>Help applying for financial aid</ListItem>
              <ListItem>Answers to any question you have</ListItem>
            </List>
          </Card.Content>
        </Card>

        {valueProps && valueProps.length > 0 && (
          <Card variant='background-white' rounded='lg'>
            <Card.Content>
              <Heading variant='h5'>{schoolName} offers</Heading>
              <List bulletStyle='bullet'>
                {valueProps.map((valueProp) => (
                  <ListItem key={valueProp.title}>{valueProp.title}</ListItem>
                ))}
              </List>
            </Card.Content>
          </Card>
        )}
      </Flex>
    </Section>
  );
};

export default ExitStrategyRecommender;
